import React, { useState, useEffect } from 'react';
import ReactPaginate from 'react-paginate';
import { useNavigate } from 'react-router-dom';
import clienteAxios from '../../helpers/axios.helper';
import Swal from 'sweetalert2';

function TablaProductos() {
  const navigate = useNavigate();
  const [productos, setProductos] = useState([]);
  const [filteredProductos, setFilteredProductos] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [searchByName, setSearchByName] = useState(true);
  const itemsPerPage = 15;

  const fetchProductos = async () => {
    try {
      const response = await clienteAxios.get('/productos/paginados', {
        params: { itemsPerPage: 1000 }, // Traer una cantidad alta de productos para filtrar en el cliente.
      });
      setProductos(response.data.productos);
      setFilteredProductos(response.data.productos);
    } catch (error) {
      console.error('Error al obtener los productos:', error);
      Swal.fire(
        'Error',
        'No se pudieron cargar los productos. Inténtalo de nuevo más tarde.',
        'error'
      );
    }
  };

  useEffect(() => {
    fetchProductos();
  }, []);

  useEffect(() => {
    // Filtrar productos al cambiar el texto de búsqueda o el tipo de búsqueda.
    const filtered = productos.filter((producto) =>
      searchByName
        ? producto.nombre.toLowerCase().includes(searchText.toLowerCase())
        : producto.codigo.toLowerCase().includes(searchText.toLowerCase())
    );
    setFilteredProductos(filtered);
    setCurrentPage(0); // Reiniciar a la primera página cuando cambian los filtros.
  }, [searchText, searchByName, productos]);

  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected);
  };

  const handleSearchTextChange = (e) => {
    setSearchText(e.target.value);
  };

  const handleSearchToggle = () => {
    setSearchByName((prev) => !prev);
    setSearchText('');
  };

  const handleRedirectAgregar = () => {
    navigate('/formulario-producto');
  };

  const handleEditar = (producto) => {
    navigate('/formulario-producto', { state: { producto, modoEdicion: true } });
  };

  const handleEliminar = async (productoId) => {
    Swal.fire({
      title: '¿Estás seguro?',
      text: '¡Esta acción no se puede deshacer!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await clienteAxios.delete(`/productos/${productoId}`);
          Swal.fire('Eliminado', 'El producto se eliminó con éxito.', 'success');
          fetchProductos();
        } catch (error) {
          console.error('Error al eliminar el producto:', error);
          Swal.fire('Error', 'No se pudo eliminar el producto.', 'error');
        }
      }
    });
  };

  // Productos de la página actual
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProductos = filteredProductos.slice(startIndex, endIndex);

  const pageCount = Math.ceil(filteredProductos.length / itemsPerPage);

  return (
    <div className="bg-white p-4 rounded-lg border-2 border-GrisOscuro">
      <div className="flex justify-between mb-4">
        <div className="flex items-center space-x-2">
          <input
            type="text"
            placeholder={`Buscar por ${searchByName ? 'nombre' : 'código'}`}
            value={searchText}
            onChange={handleSearchTextChange}
            className="border-2 border-gray-300 p-2 rounded"
          />
          <label className="flex items-center space-x-1">
            <input
              type="checkbox"
              checked={searchByName}
              onChange={handleSearchToggle}
            />
            <span>Buscar por {searchByName ? 'nombre' : 'código'}</span>
          </label>
        </div>
        <button
          onClick={handleRedirectAgregar}
          className="py-2 px-4 bg-Verde text-white font-semibold rounded hover:bg-green-600"
        >
          Agregar Producto
        </button>
      </div>

      <table className="min-w-full bg-white border">
        <thead>
          <tr>
            <th className="text-start py-2 px-4 border-b">Código</th>
            <th className="text-start py-2 px-4 border-b">Nombre</th>
            <th className="text-start py-2 px-4 border-b">Precio</th>
            <th className="text-start py-2 px-4 border-b">Estado</th>
            <th className="text-start py-2 px-4 border-b">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {currentProductos.length > 0 ? (
            currentProductos.map((producto) => (
              <tr key={producto.codigo}>
                <td className="py-2 px-4 border-b">{producto.codigo}</td>
                <td className="py-2 px-4 border-b">{producto.nombre}</td>
                <td className="py-2 px-4 border-b">
                  {producto.mostrar_precio_dolar
                    ? `US$ ${Number(producto.precioventadolar).toLocaleString('es-AR')}`
                    : `AR$ ${Number(producto.precioventapeso).toLocaleString('es-AR')}`}
                </td>
                <td className="py-2 px-4 border-b">
                  {producto.disponible ? 'Disponible' : 'A Pedido'}
                </td>
                <td className="py-2 px-4 border-b">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditar(producto)}
                      className="px-3 py-1 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleEliminar(producto.id)}
                      className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                    >
                      Eliminar
                    </button>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="5" className="text-center py-4">
                No se encontraron productos.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="flex justify-center mt-4">
        <ReactPaginate
          breakLabel="..."
          nextLabel=">"
          onPageChange={handlePageClick}
          pageRangeDisplayed={5}
          pageCount={pageCount || 1}
          previousLabel="<"
          containerClassName="flex justify-center items-center"
          pageClassName="p-1 px-3 text-Negro bg-GrisClaro"
          activeClassName="bg-GrisOscuro text-Blanco"
          previousClassName="px-2 py-1 text-Rojo font-bold text-xl"
          nextClassName="px-2 py-1 text-Rojo font-bold text-xl"
          breakClassName="px-2 py-1 text-Rojo"
        />
      </div>
    </div>
  );
}

export default TablaProductos;
